import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

export default function Tour() {
  const [tour, setTour] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setTour(r.data.tour)).catch(() => {});
  }, []);

  if (!tour) return <div className="min-h-screen bg-background" data-testid="tour-loading" />;

  const features = (tour.features || []).filter((f) => f.enabled !== false);

  return (
    <div data-testid="tour-page" className="min-h-screen">
      <section className="max-w-5xl mx-auto px-6 pt-16 sm:pt-24 pb-8 text-center">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="text-xs tracking-[0.3em] uppercase text-primary" data-testid="tour-eyebrow">
          {tour.hero_eyebrow}
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="font-outfit text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight" data-testid="tour-title">
          {tour.hero_title}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
          className="text-muted-foreground text-base md:text-lg mt-5 max-w-2xl mx-auto">
          {tour.hero_subtitle}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}
          className="flex flex-wrap justify-center gap-3 mt-8">
          <Link to="/demo">
            <Button data-testid="tour-demo-cta" className="rounded-full px-6">
              <PlayCircle className="w-4 h-4 mr-2" /> Try the live demo
            </Button>
          </Link>
          <Link to="/register">
            <Button data-testid="tour-register-cta" variant="outline" className="rounded-full px-6">
              List your restaurant <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-12 space-y-16 sm:space-y-24">
        {features.map((f, i) => (
          <motion.div key={i} data-testid={`tour-feature-${i}`}
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5 }}
            className={`flex flex-col ${i % 2 ? "md:flex-row-reverse" : "md:flex-row"} items-center gap-8 md:gap-14`}>
            <div className="flex-1 w-full">
              <img src={f.image} alt={f.title} loading="lazy" data-testid={`tour-feature-img-${i}`}
                className="w-full rounded-2xl border border-border/60 shadow-xl bg-card" />
            </div>
            <div className="flex-1">
              <p className="text-xs tracking-[0.25em] uppercase text-primary">{f.tag}</p>
              <h2 className="font-outfit text-2xl sm:text-3xl mt-2">{f.title}</h2>
              <p className="text-muted-foreground mt-3 leading-relaxed">{f.body}</p>
            </div>
          </motion.div>
        ))}
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-24 text-center">
        <div className="rounded-3xl border border-border/60 bg-card p-10 sm:p-14">
          <h2 className="font-outfit text-3xl sm:text-4xl" data-testid="tour-cta-title">{tour.cta_title}</h2>
          <p className="text-muted-foreground mt-3">{tour.cta_subtitle}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <Link to="/demo">
              <Button data-testid="tour-demo-cta-bottom" className="rounded-full px-6">
                <PlayCircle className="w-4 h-4 mr-2" /> Launch the live demo
              </Button>
            </Link>
            <Link to="/register">
              <Button data-testid="tour-register-cta-bottom" variant="outline" className="rounded-full px-6">
                List your restaurant <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
